<template>
  <section class="promotion-products">
    <div class="promotion-products__header mb-1">
      <h5 class="mb-25">{{ promotionDescription }}</h5>
    </div>

    <div class="promotion-products__box">
      <b-row>
        <b-col cols="12" md="4">
          <b-form-group
            :label="$t(label('promotionType'))"
            :label-for="`${idPrefix}-promotion-type`"
          >
            <v-select
              :id="`${idPrefix}-promotion-type`"
              :value="localPromotion.idCatTypePromotion"
              :options="normalizedPromotionTypes"
              label="name"
              :placeholder="$t(label('promotionType'))"
              :disabled="disabled"
              :reduce="(option) => option.id"
              @input="onTypeChange"
            >
              <span slot="no-options">{{ $t("sorryOption") }}</span>
            </v-select>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4">
          <b-form-group
            :label="$t(label('promotionStartDate'))"
            :label-for="`${idPrefix}-promotion-start-date`"
          >
            <b-form-input
              :id="`${idPrefix}-promotion-start-date`"
              :value="formatDateForInput(localPromotion.promotionStartDate)"
              type="date"
              :disabled="disabled || !hasConfigurablePromotion"
              @input="updateField('promotionStartDate', $event)"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4">
          <b-form-group
            :label="$t(label('promotionEndDate'))"
            :label-for="`${idPrefix}-promotion-end-date`"
          >
            <b-form-input
              :id="`${idPrefix}-promotion-end-date`"
              :value="formatDateForInput(localPromotion.promotionEndDate)"
              type="date"
              :disabled="disabled || !hasConfigurablePromotion"
              @input="updateField('promotionEndDate', $event)"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <transition name="promotion-products-fade" mode="out-in">
        <b-row v-if="isFixedPricePromotion" key="fixed-price" class="mt-50">
          <b-col cols="12" md="6">
            <b-form-group
              :label="$t(label('priceBefore'))"
              :label-for="`${idPrefix}-price-before`"
            >
              <b-form-input
                :id="`${idPrefix}-price-before`"
                :value="localPromotion.priceBefore"
                type="number"
                step="0.01"
                min="0"
                :placeholder="$t(label('priceBeforePlaceholder'))"
                :disabled="disabled"
                @input="updateField('priceBefore', $event)"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group
              :label="$t(label('priceNow'))"
              :label-for="`${idPrefix}-price-now`"
            >
              <b-form-input
                :id="`${idPrefix}-price-now`"
                :value="localPromotion.priceNow"
                type="number"
                step="0.01"
                min="0"
                :placeholder="$t(label('priceNowPlaceholder'))"
                :disabled="disabled"
                @input="updateField('priceNow', $event)"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" class="d-flex align-items-end">
            <div class="promotion-products__simulation w-100">
              <span>{{ $t(label('pricePreview')) }}</span>
              <strong>
                <small class="promotion-products__base-price">
                  {{ formatMoney(localPromotion.priceBefore) }}
                </small>
                {{ formatMoney(localPromotion.priceNow) }}
              </strong>
            </div>
          </b-col>
        </b-row>

        <b-row v-else-if="isPercentagePromotion" key="percentage" class="mt-50">
          <b-col cols="12" md="6">
            <b-form-group
              :label="$t(label('discountPercentage'))"
              :label-for="`${idPrefix}-discount-percentage`"
            >
              <b-form-input
                :id="`${idPrefix}-discount-percentage`"
                :value="localPromotion.discountPercentage"
                type="number"
                min="1"
                max="100"
                :placeholder="$t(label('discountPercentagePlaceholder'))"
                :disabled="disabled"
                @input="updateField('discountPercentage', $event)"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6" class="d-flex align-items-end">
            <div class="promotion-products__simulation w-100">
              <span>{{ $t(label('finalPrice')) }}</span>
              <strong>{{ formatMoney(percentageFinalPrice) }}</strong>
            </div>
          </b-col>
        </b-row>

        <b-row v-else-if="isVolumePromotion" key="volume" class="mt-50">
          <b-col cols="12" md="6">
            <b-form-group
              :label="$t(label('promotionMinQuantity'))"
              :label-for="`${idPrefix}-promotion-min-quantity`"
            >
              <b-form-input
                :id="`${idPrefix}-promotion-min-quantity`"
                :value="localPromotion.promotionMinQuantity"
                type="number"
                min="2"
                :placeholder="$t(label('promotionMinQuantityPlaceholder'))"
                :disabled="disabled"
                @input="updateField('promotionMinQuantity', $event)"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group
              :label="$t(label('quantityPrice'))"
              :label-for="`${idPrefix}-quantity-price`"
            >
              <b-form-input
                :id="`${idPrefix}-quantity-price`"
                :value="localPromotion.priceNow"
                type="number"
                step="0.01"
                min="0"
                :placeholder="$t(label('quantityPricePlaceholder'))"
                :disabled="disabled"
                @input="updateField('priceNow', $event)"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" class="d-flex align-items-center">
            <p class="promotion-products__hint mb-0">
              {{ $t(label('quantityHelp')) }}
            </p>
          </b-col>
        </b-row>

        <b-row v-else-if="isComboPromotion" key="combo" class="mt-50">
          <b-col cols="12" md="6">
            <b-form-group
              :label="$t(label('comboQuantity'))"
              :label-for="`${idPrefix}-combo-quantity-of-products`"
            >
              <b-form-input
                :id="`${idPrefix}-combo-quantity-of-products`"
                :value="localPromotion.quantityOfProducts"
                type="number"
                min="2"
                :placeholder="$t(label('comboQuantityPlaceholder'))"
                :disabled="disabled"
                @input="updateField('quantityOfProducts', $event)"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group
              :label="$t(label('comboPrice'))"
              :label-for="`${idPrefix}-combo-price`"
            >
              <b-form-input
                :id="`${idPrefix}-combo-price`"
                :value="localPromotion.priceNow"
                type="number"
                step="0.01"
                min="0"
                :placeholder="$t(label('comboPricePlaceholder'))"
                :disabled="disabled"
                @input="updateField('priceNow', $event)"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div v-else key="empty" class="promotion-products__empty mt-50">
          {{ $t(label('noPromotionHelp')) }}
        </div>
      </transition>

      <b-row v-if="isVolumePromotion" class="mt-1">
        <b-col cols="12">
          <b-form-checkbox
            :checked="localPromotion.isCumulative"
            :disabled="disabled"
            @change="updateField('isCumulative', $event)"
          >
            {{ cumulativeLabel }}
          </b-form-checkbox>
        </b-col>
      </b-row>
    </div>
  </section>
</template>

<script>
import {
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BRow,
} from "bootstrap-vue";
import vSelect from "vue-select";

const PROMOTION_TYPE = {
  none: "",
};

export default {
  name: "PromotionProducts",
  components: {
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BRow,
    vSelect,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    basePrice: {
      type: [Number, String],
      default: 0,
    },
    currency: {
      type: String,
      default: "$",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    description: {
      type: String,
      default: "",
    },
    idPrefix: {
      type: String,
      default: "promotion-products",
    },
    promotionTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localPromotion: {},
      promotionType: PROMOTION_TYPE,
    };
  },
  computed: {
    normalizedPromotionTypes() {
      return [
        {
          id: PROMOTION_TYPE.none,
          name: this.$t(this.label("selectPromotion")),
        },
        ...this.promotionTypes,
      ];
    },
    normalizedBasePrice() {
      return this.toNumber(this.basePrice);
    },
    selectedPromotionType() {
      return this.normalizedPromotionTypes.find(
        (type) =>
          String(type.id) === String(this.localPromotion.idCatTypePromotion),
      );
    },
    selectedPromotionName() {
      return this.normalizeText(
        this.selectedPromotionType ? this.selectedPromotionType.name : "",
      );
    },
    hasConfigurablePromotion() {
      return (
        !!this.localPromotion.idCatTypePromotion &&
        !this.isNoPromotionType(this.localPromotion.idCatTypePromotion)
      );
    },
    isPercentagePromotion() {
      return (
        this.selectedPromotionName.includes("porcentaje") ||
        this.selectedPromotionName.includes("% off")
      );
    },
    isVolumePromotion() {
      return (
        this.selectedPromotionName.includes("volumen") ||
        this.selectedPromotionName.includes("cantidad")
      );
    },
    isComboPromotion() {
      return (
        this.selectedPromotionName.includes("combo") ||
        this.selectedPromotionName.includes("kit")
      );
    },
    isFixedPricePromotion() {
      return (
        this.selectedPromotionName.includes("precio fijo") ||
        (this.selectedPromotionName.includes("antes") &&
          this.selectedPromotionName.includes("ahora"))
      );
    },
    percentageFinalPrice() {
      const percentage = this.toNumber(this.localPromotion.discountPercentage);
      const discount = this.normalizedBasePrice * (percentage / 100);
      return Math.max(this.normalizedBasePrice - discount, 0);
    },
    cumulativeLabel() {
      return this.localPromotion.isCumulative
        ? this.$t(this.label("isCumulative"))
        : this.$t(this.label("notCumulative"));
    },
    promotionDescription() {
      return this.description || this.$t(this.label("promotionDescription"));
    },
  },
  watch: {
    basePrice() {
      this.syncPercentagePriceNow();
    },
    value: {
      deep: true,
      immediate: true,
      handler(value) {
        const normalized = this.normalizePromotion(value);

        if (
          JSON.stringify(normalized) !== JSON.stringify(this.localPromotion)
        ) {
          this.localPromotion = normalized;
        }
      },
    },
  },
  methods: {
    label(field) {
      return `pages.products.form.label.${field}`;
    },
    normalizePromotion(value = {}) {
      const promotion = value || {};
      const isVolumePromotion = this.isVolumePromotionType(
        promotion.idCatTypePromotion,
      );

      return {
        ...promotion,
        idCatTypePromotion: promotion.idCatTypePromotion || PROMOTION_TYPE.none,
        isCumulative: isVolumePromotion
          ? promotion.isCumulative === undefined
            ? false
            : promotion.isCumulative
          : false,
      };
    },
    onTypeChange(type) {
      const isNoPromotion = this.isNoPromotionType(type);

      if (isNoPromotion) {
        this.updatePromotion({
          idCatTypePromotion: type,
          priceBefore: "",
          priceNow: "",
          discountPercentage: "",
          quantityOfProducts: "",
          promotionStartDate: "",
          promotionEndDate: "",
          promotionMinQuantity: "",
          isCumulative: false,
        });
        this.$emit("type-change", type);
        return;
      }

      this.updatePromotion({
        ...this.resetTypeFields(this.localPromotion),
        idCatTypePromotion: type,
        isCumulative: false,
      });
      this.$emit("type-change", type);
    },
    updateField(field, value) {
      if (field === "discountPercentage" && this.isPercentagePromotion) {
        if (value === "") {
          this.updatePromotion({
            ...this.localPromotion,
            discountPercentage: "",
            priceBefore: "",
            priceNow: "",
          });
          return;
        }

        this.updatePromotion({
          ...this.localPromotion,
          discountPercentage: value,
          priceBefore: this.normalizedBasePrice,
          priceNow: this.calculateDiscountedPrice(value),
        });
        return;
      }

      this.updatePromotion({
        ...this.localPromotion,
        [field]: value,
      });
    },
    updatePromotion(promotion) {
      const normalized = this.normalizePromotion(promotion);
      this.localPromotion = normalized;

      this.$emit("input", normalized);
      this.$emit("change", {
        value: normalized,
        payload: normalized,
      });
    },
    resetTypeFields(promotion = {}) {
      return {
        ...promotion,
        priceNow: "",
        discountPercentage: "",
        promotionMinQuantity: "",
      };
    },
    normalizeText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    getPromotionNameById(idCatTypePromotion) {
      const promotionType = this.normalizedPromotionTypes.find(
        (type) => String(type.id) === String(idCatTypePromotion),
      );

      return this.normalizeText(promotionType ? promotionType.name : "");
    },
    isNoPromotionType(idCatTypePromotion) {
      const name = this.getPromotionNameById(idCatTypePromotion);

      return !idCatTypePromotion || name.includes("sin promocion");
    },
    isVolumePromotionType(idCatTypePromotion) {
      const name = this.getPromotionNameById(idCatTypePromotion);

      return name.includes("volumen") || name.includes("cantidad");
    },
    toNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },
    calculateDiscountedPrice(discountPercentage) {
      const percentage = this.toNumber(discountPercentage);
      const discount = this.normalizedBasePrice * (percentage / 100);

      return Math.max(this.normalizedBasePrice - discount, 0);
    },
    syncPercentagePriceNow() {
      if (
        !this.isPercentagePromotion ||
        this.localPromotion.discountPercentage === ""
      ) {
        return;
      }

      this.updatePromotion({
        ...this.localPromotion,
        priceBefore: this.normalizedBasePrice,
        priceNow: this.calculateDiscountedPrice(
          this.localPromotion.discountPercentage,
        ),
      });
    },
    formatMoney(value) {
      return `${this.currency}${this.toNumber(value).toFixed(2)}`;
    },
    formatDateForInput(value) {
      if (!value) return "";

      return String(value).split("T")[0];
    },
  },
};
</script>

<style lang="scss" scoped>
.promotion-products {
  &__header h5 {
    color: #5e5873;
    font-weight: 600;
  }

  &__box {
    padding: 1.25rem;
    border: 1px dashed #f59e0b;
    border-radius: 0.75rem;
  }

  &__simulation {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 42px;
    padding: 0.75rem 1rem;
    border: 1px solid #bbf7d0;
    border-radius: 0.5rem;
    background: #f0fdf4;
    color: #5e5873;
  }

  &__simulation strong {
    color: #10b981;
    font-size: 1rem;
    white-space: nowrap;
  }

  &__base-price {
    margin-right: 0.35rem;
    color: #6e6b7b;
    font-size: 0.75rem;
    font-weight: 500;
    text-decoration: line-through;
  }

  &__hint,
  &__empty {
    color: #6e6b7b;
    font-size: 0.85rem;
  }

  &__empty {
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    background: rgba(255, 255, 255, 0.65);
  }
}

.promotion-products-fade-enter-active,
.promotion-products-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.promotion-products-fade-enter,
.promotion-products-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
